import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { User } from '@/features/participant';
import { chatEE } from '@/layout';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { Conversation } from '../models/conversation';
import { ConversationCreate } from '../models/conversation-dto';
import { conversationKeys } from '../queries/conversation-keys';
import { useCreateConversation } from '../queries/conversation.mutations';

export interface DirectConvPayload {
  user: User;
}

export const useConversationCreateDirectListener = () => {
  const { mutateAsync } = useCreateConversation();
  const { user } = useAuthStore();
  const setActiveConversation = useUIStore((state) => state.setActiveConversation);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleCreateDirectChat = async (data: DirectConvPayload) => {
      // Check if the conversation already exists
      const query = queryClient.getQueryData<InfiniteData<Conversation[]>>(conversationKeys.all);
      const conversation = query?.pages
        .flat()
        .find((conv) => conv.conversationType === 'DIRECT' && conv.partnerId === data.user.id);
      if (conversation) {
        setActiveConversation(conversation);
        return;
      }

      if (!data?.user) return;
      const res = await mutateAsync(
        new ConversationCreate({
          conversationType: 'DIRECT',
          conversationName: data.user.fullName,
          participantIds: [data.user.id, user!.user_id],
          parentConversationId: undefined,
          metadata: undefined
        })
      );
      setActiveConversation(res);
    };

    chatEE.on('conversation-create-direct', handleCreateDirectChat);

    return () => {
      chatEE.off('conversation-create-direct', handleCreateDirectChat);
    };
  }, []);
};
